import dayjs, { Dayjs } from "dayjs";
import {
  getEventId,
  getIsAllDayEvent,
  isEndOfDay,
  mergeInactiveEvents,
} from "common/Calendars/utils.ts";
import { OperationHoursEvent } from "@shared/types";
import { Dispatch, SetStateAction, useEffect, useState } from "react";
import { MAX_DAY_END_TIME } from "common/Calendars/constants.ts";
import { SCROLLBAR_SIZE } from "@shared/common/Themes/AppTheme.ts";
import { useBottomNavigator } from "utils/hooks/misc.tsx";
import useMedia from "utils/hooks/useMedia.ts";
import useEventTimePicker from "common/Calendars/useEventTimePicker.ts";

interface EventPopoverProps {
  showPopover: boolean;
  setShowPopover: (showPopover: boolean) => void;
  selectedEvent: OperationHoursEvent | null;
  setSelectedEvent: (selectedEvent: OperationHoursEvent | null) => void;
  setMyEvents?: Dispatch<SetStateAction<OperationHoursEvent[] | undefined>>;
}

const useEventPopover = ({
  showPopover,
  setShowPopover,
  selectedEvent,
  setSelectedEvent,
  setMyEvents,
}: EventPopoverProps) => {
  const { baseShouldDisplay: isBottomNavigator } = useBottomNavigator();
  const isSmallScreen = useMedia([`(max-width: 900px)`], [true], false);
  const { props: timePickerProps } = useEventTimePicker();

  const [isTimePickerOpen, setIsTimePickerOpen] = useState(false);

  useEffect(() => {
    const calendarWrapper = document.getElementById("calender-wrapper");
    if (calendarWrapper) {
      calendarWrapper.style.overflow = showPopover ? "hidden" : "auto";
      calendarWrapper.style.marginRight = `${
        showPopover && !isBottomNavigator ? SCROLLBAR_SIZE : 0
      }px`;
    }
  }, [showPopover]);

  const editEvent = (values: [Dayjs | null, Dayjs | null] | null) => {
    if (!values) return;

    const [startDayjs, endDayjs] = values;
    const start = startDayjs?.toDate();
    const end = endDayjs?.toDate();
    if (!start || !end) return;

    if (isEndOfDay(end)) end.setHours(...MAX_DAY_END_TIME);

    setMyEvents?.((prev) => {
      let events: OperationHoursEvent[] = [];
      const updatedEvents = prev?.filter(
        (event) => event.id !== selectedEvent?.id
      );

      if (start.getTime() === end.getTime()) {
        events = updatedEvents || [];
      } else {
        const editedEvent = {
          ...selectedEvent,
          start,
          end,
          id: getEventId(start, end),
        };
        events = [...(updatedEvents || []), editedEvent];
      }

      handlePopoverClose();
      return mergeInactiveEvents(events);
    });
  };

  const onAllDayClick = () => {
    if (
      (selectedEvent && getIsAllDayEvent(selectedEvent)) ||
      !selectedEvent?.start ||
      !selectedEvent?.end
    )
      return;
    const start = selectedEvent?.start.setHours(0, 0);
    const end = selectedEvent?.end.setHours(23, 59);
    editEvent([dayjs(start), dayjs(end)]);
  };

  const handlePopoverClose = () => {
    setShowPopover(false);
    setIsTimePickerOpen(false);
    setSelectedEvent(null);
  };

  return {
    editEvent,
    onAllDayClick,
    handlePopoverClose,
    isBottomNavigator,
    isSmallScreen,
    timePickerProps,
    isTimePickerOpen,
    setIsTimePickerOpen,
  };
};

export default useEventPopover;
